import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';
import {
    Code,
    Group,
    Text,
    Tooltip,
} from '@mantine/core';

import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';
import {
    DefectSignalBadge,
    DefectStatusBadge,
} from '../profile/Badges.tsx';
import {DefectSignature} from '../types';
import config from '../config';

// EVERY column is sortable — the platform rule, and here it is also the primary
// triage tool: "what is breaking now" is a lastSeenAt sort, "what is worst" is
// an occurrenceCount sort, "what is spreading" is a distinctUserCount sort.
export const useDefectSignaturesTableColumns = () => {

    const columns: DataTableColumn<DefectSignature>[] = [
        {
            accessor: 'lastSeenAt',
            title: I18n.get('Last seen'),
            width: '180px',
            sortable: true,
            render: (record: DefectSignature) => (
                <Text size={'sm'}>{formatTimestamp(record.lastSeenAt)}</Text>
            ),
        },
        {
            accessor: 'actionCode',
            title: I18n.get('Action'),
            width: '1fr',
            sortable: true,
            render: (record: DefectSignature) => (
                <NavLink to={`${config.basePath}/${encodeURIComponent(record.id)}`}>
                    <Code>{record.actionCode}</Code>
                </NavLink>
            ),
        },
        {
            accessor: 'verificationResult',
            title: I18n.get('Signal'),
            width: '160px',
            sortable: true,
            // NOT the raw enum — the classified kind, so NOT_VERIFIED reads as
            // "Unverified effect" and a returning defect reads as "Regression".
            render: (record: DefectSignature) => <DefectSignalBadge record={record}/>,
        },
        {
            accessor: 'occurrenceCount',
            title: I18n.get('Occurrences'),
            width: '130px',
            sortable: true,
            textAlign: 'right',
            render: (record: DefectSignature) => (
                <Text size={'sm'}>{record.occurrenceCount}</Text>
            ),
        },
        {
            accessor: 'distinctUserCount',
            title: I18n.get('Users'),
            width: '100px',
            sortable: true,
            textAlign: 'right',
            // The number that separates "one flaky user" from "everyone is
            // hitting this". Given its own column rather than folded into the
            // occurrence count for exactly that reason.
            render: (record: DefectSignature) => (
                <Tooltip
                    label={`${record.distinctUserCount} distinct synthetic ${record.distinctUserCount === 1 ? 'user has' : 'users have'} hit this defect`}
                    withArrow
                >
                    <Text size={'sm'}>{record.distinctUserCount}</Text>
                </Tooltip>
            ),
        },
        {
            accessor: 'sampleError',
            title: I18n.get('Detail'),
            width: '2fr',
            sortable: true,
            render: (record: DefectSignature) => {
                const text = record.normalizedError ?? record.sampleError;
                if (!text) {
                    return <Text size={'xs'} c={'dimmed'}>—</Text>;
                }
                return (
                    // The NORMALIZED form is shown, not the raw sample: it is the
                    // reason these rows are one defect, and its placeholders make
                    // the shared shape legible at a glance. The verbatim sample is
                    // one hover away and on the detail page.
                    <Tooltip label={record.sampleError ?? text} multiline w={420} withArrow>
                        <Text size={'xs'} lineClamp={2}>{text}</Text>
                    </Tooltip>
                );
            },
        },
        {
            accessor: 'firstSeenAt',
            title: I18n.get('First seen'),
            width: '180px',
            sortable: true,
            render: (record: DefectSignature) => (
                <Text size={'sm'} c={'dimmed'}>{formatTimestamp(record.firstSeenAt)}</Text>
            ),
        },
        {
            accessor: 'status',
            title: I18n.get('Triage'),
            width: '140px',
            sortable: true,
            render: (record: DefectSignature) => <DefectStatusBadge status={record.status}/>,
        },
        {
            accessor: 'jiraKey',
            title: I18n.get('Ticket'),
            width: '130px',
            sortable: true,
            render: (record: DefectSignature) => {
                if (!record.jiraKey) {
                    return <Text size={'xs'} c={'dimmed'}>—</Text>;
                }
                return (
                    <Group gap={'xs'} wrap={'nowrap'}>
                        <Text
                            size={'xs'}
                            ff={'monospace'}
                            component={'a'}
                            href={`https://flexinetwork.atlassian.net/browse/${record.jiraKey}`}
                            target={'_blank'}
                            rel={'noreferrer'}
                            // Stops the row's own navigate handler from firing and
                            // sending the operator to the detail page instead.
                            onClick={(event) => event.stopPropagation()}
                        >
                            {record.jiraKey}
                        </Text>
                    </Group>
                );
            },
        },
    ];

    return {
        columns,
    };
};
